import { APIProvider, AuthService } from "../../AppController";
import { ONBOARDING_FLOW } from "./flow";
import { OnboardingState, OnboardingStatus, OnboardingStepKey } from "./types";
import {
  PLACEHOLDER_DOB,
  PLACEHOLDER_FIRST_NAME,
  PLACEHOLDER_HEIGHT_CM,
  PLACEHOLDER_LAST_NAME,
  PLACEHOLDER_WEIGHT_KG,
} from "../../util/placeholderName";

const ROUTES = {
  state: "/patient-centric-app-v2/onboarding/me",
  legacyStep: "/patient-centric-app-v2/onboarding/steps/personalize",
  legacyComplete: "/patient-centric-app-v2/onboarding/complete",
  legacyReset: "/patient-centric-app-v2/onboarding/reset",
  sendEmailOtp: "/patient-centric-app-v2/send-email-otp",
  // Auth-gated account email verification: verifies the code AND sets the
  // email_verified flag in users.user_preference (distinct from the shared,
  // unauthenticated /verify-otp used by other OTP flows).
  verifyOtp: "/patient-centric-app-v2/verify-email-otp",
  updateUserName: "/patient-centric-app-v2/user/name",
  // Non-admin, self-serve teammate invite. Same call shape as the Admin Panel's
  // `POST /admin/v3/users` (send_invitation flow), just on the CIP-scoped path.
  inviteUsers: "/patient-centric-app-v2/cip/v1/user-invite",
  // Member "recommend CIP" referral. Sends a soft intro email (CIP link + "email
  // support@circadia.health for access") instead of creating accounts.
  // NOTE: backend endpoint is NOT implemented yet — this is the intended contract
  // the frontend targets; calls will 404 until the backend lands.
  referColleagues: "/patient-centric-app-v2/cip/v1/refer-colleagues",
} as const;

export type Result<T> = {
  success: boolean;
  data?: T;
  error?: { message: string };
};

const LEGACY_STATE_KEY = "__cip_frontend_onboarding_state";

const messageFrom = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const unwrap = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }
  return response as T;
};

const authHeaders = async () => ({
  Authorization: `Bearer ${await AuthService.getAuthenticationToken()}`,
});

const firstWizardKey = (ONBOARDING_FLOW.steps.find(
  (step) => step.showInProgress
)?.key ?? ONBOARDING_FLOW.steps[0].key) as OnboardingStepKey;

const initialState = (): OnboardingState => ({
  flowKey: ONBOARDING_FLOW.flowKey,
  flowVersion: ONBOARDING_FLOW.version,
  status: "not_started",
  currentStepKey: firstWizardKey,
  answers: {},
  lastPlatform: "web",
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isStatus = (value: unknown): value is OnboardingStatus =>
  value === "not_started" || value === "in_progress" || value === "completed";

const isOnboardingState = (value: unknown): value is OnboardingState =>
  isRecord(value) &&
  typeof value.flowKey === "string" &&
  typeof value.flowVersion === "number" &&
  isStatus(value.status) &&
  typeof value.currentStepKey === "string" &&
  isRecord(value.answers) &&
  typeof value.lastPlatform === "string";

const responseError = (response: unknown): string | null => {
  if (!isRecord(response) || !("detail" in response)) return null;
  return typeof response.detail === "string"
    ? response.detail
    : "Onboarding request failed";
};

const isUnsupportedWrite = (response: unknown): boolean => {
  const message = responseError(response);
  return !!message && /not found|method not allowed/i.test(message);
};

const normalizeLegacyState = (state: OnboardingState): OnboardingState => {
  const answers = { ...state.answers };
  const savedFrontendState = answers[LEGACY_STATE_KEY];
  delete answers[LEGACY_STATE_KEY];
  if (
    !isRecord(savedFrontendState) ||
    typeof savedFrontendState.flowKey !== "string" ||
    typeof savedFrontendState.flowVersion !== "number" ||
    !isStatus(savedFrontendState.status) ||
    typeof savedFrontendState.currentStepKey !== "string" ||
    typeof savedFrontendState.lastPlatform !== "string"
  ) {
    return { ...state, answers };
  }
  return {
    ...state,
    flowKey: savedFrontendState.flowKey,
    flowVersion: savedFrontendState.flowVersion,
    status: savedFrontendState.status,
    currentStepKey: savedFrontendState.currentStepKey as OnboardingStepKey,
    answers,
    lastPlatform:
      savedFrontendState.lastPlatform as OnboardingState["lastPlatform"],
  };
};

const putLegacyState = async (
  state: OnboardingState
): Promise<Result<OnboardingState>> => {
  const headers = await authHeaders();
  if (state.status === "not_started") {
    const response = await APIProvider.POST(ROUTES.legacyReset, {}, headers);
    const error = responseError(response);
    return !isOnboardingState(unwrap<unknown>(response))
      ? {
          success: false,
          error: { message: error || "Unable to reset onboarding" },
        }
      : { success: true, data: state };
  }

  const response = await APIProvider.POST(
    ROUTES.legacyStep,
    {
      answers: {
        ...state.answers,
        [LEGACY_STATE_KEY]: {
          flowKey: state.flowKey,
          flowVersion: state.flowVersion,
          status: state.status,
          currentStepKey: state.currentStepKey,
          lastPlatform: state.lastPlatform,
        },
      },
      platform: state.lastPlatform,
    },
    headers
  );
  const stepError = responseError(response);
  if (!isOnboardingState(unwrap<unknown>(response))) {
    return {
      success: false,
      error: { message: stepError || "Unable to save onboarding progress" },
    };
  }

  if (state.status === "completed") {
    const completion = await APIProvider.POST(
      ROUTES.legacyComplete,
      {},
      headers
    );
    const completionError = responseError(completion);
    if (!isOnboardingState(unwrap<unknown>(completion))) {
      return {
        success: false,
        error: {
          message: completionError || "Unable to complete onboarding",
        },
      };
    }
  }
  return { success: true, data: state };
};

/** Live persistence, with a compatibility path for the pre-PUT backend. */
export const liveOnboardingController = {
  getState: async (): Promise<Result<OnboardingState>> => {
    try {
      const response = await APIProvider.GET(
        ROUTES.state,
        {},
        await authHeaders()
      );
      const state = unwrap<unknown>(response);
      if (isOnboardingState(state)) {
        return { success: true, data: normalizeLegacyState(state) };
      }
      if (isUnsupportedWrite(response)) {
        return { success: true, data: initialState() };
      }
      return {
        success: false,
        error: {
          message:
            responseError(response) || "Unable to load onboarding progress",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: messageFrom(error, "Unable to load onboarding progress"),
        },
      };
    }
  },
  putState: async (
    state: OnboardingState
  ): Promise<Result<OnboardingState>> => {
    try {
      const response = await APIProvider.PUT(
        ROUTES.state,
        {
          flowKey: state.flowKey,
          flowVersion: state.flowVersion,
          status: state.status,
          currentStepKey: state.currentStepKey,
          answers: state.answers,
          lastPlatform: state.lastPlatform,
        },
        await authHeaders()
      );
      const saved = unwrap<unknown>(response);
      if (isOnboardingState(saved)) {
        return { success: true, data: normalizeLegacyState(saved) };
      }
      if (isUnsupportedWrite(response)) return putLegacyState(state);
      return {
        success: false,
        error: {
          message:
            responseError(response) || "Unable to save onboarding progress",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: messageFrom(error, "Unable to save onboarding progress"),
        },
      };
    }
  },
};

// Pre-auth state is deliberately memory-only so one browser user cannot see
// another user's abandoned email/name/phone answers.
let bootstrapState = initialState();

export const bootstrapOnboardingController = {
  getState: async (): Promise<Result<OnboardingState>> => ({
    success: true,
    data: bootstrapState,
  }),
  putState: async (
    state: OnboardingState
  ): Promise<Result<OnboardingState>> => {
    bootstrapState = state;
    return { success: true, data: state };
  },
};

export const clearBootstrapOnboardingState = (): void => {
  bootstrapState = initialState();
};

type EmailOtpSendResponse = {
  message: string;
  status: boolean;
  unique_request_id: string;
};

export const emailOtpController = {
  sendEmailOtp: async (): Promise<Result<EmailOtpSendResponse>> => {
    try {
      const response = (await APIProvider.POST(
        ROUTES.sendEmailOtp,
        {},
        await authHeaders()
      )) as EmailOtpSendResponse | { detail?: unknown } | undefined;
      if (!response || !("status" in response) || !response.status) {
        const detail =
          response &&
          "detail" in response &&
          typeof response.detail === "string"
            ? response.detail
            : "Failed to send verification code";
        return { success: false, error: { message: detail } };
      }
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: {
          message: messageFrom(error, "Failed to send verification code"),
        },
      };
    }
  },
  verifyEmailOtp: async (payload: {
    uniqueRequestId: string;
    otp: string;
  }): Promise<Result<{ message: string }>> => {
    try {
      const response = (await APIProvider.POST(
        ROUTES.verifyOtp,
        {
          otp: payload.otp,
          unique_request_id: payload.uniqueRequestId,
        },
        await authHeaders()
      )) as { message?: string };
      return {
        success: response?.message === "OTP Verified!",
        data: { message: response?.message ?? "" },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: messageFrom(error, "Failed to verify code") },
      };
    }
  },
};

export const updateOnboardingUserName = async (payload: {
  first_name: string;
  last_name: string;
}): Promise<Result<unknown>> => {
  try {
    const response = await APIProvider.PUT(
      ROUTES.updateUserName,
      payload,
      await authHeaders()
    );
    const error = responseError(response);
    return response && !error
      ? { success: true, data: response }
      : {
          success: false,
          error: { message: error || "Unable to save your name" },
        };
  } catch (error) {
    return {
      success: false,
      error: { message: messageFrom(error, "Unable to save your name") },
    };
  }
};

/** Validates the explicit 200 response contract from the invite endpoint. */
const inviteErrorMessage = (response: unknown): string | null => {
  if (
    isRecord(response) &&
    response.invitation_sent === true &&
    isRecord(response.data)
  ) {
    return null;
  }
  const detail = responseError(response);
  if (detail) return detail;
  if (isRecord(response) && "message" in response) {
    const message = response.message;
    if (Array.isArray(message)) return message.join("\n");
    if (typeof message === "string") return message;
  }
  return "Failed to send invitation";
};

/** Validates the 200 response contract from the refer-colleagues endpoint. */
const referralErrorMessage = (response: unknown): string | null => {
  if (isRecord(response) && response.recommendation_sent === true) {
    return null;
  }
  const detail = responseError(response);
  if (detail) return detail;
  if (isRecord(response) && "message" in response) {
    const message = response.message;
    if (Array.isArray(message)) return message.join("\n");
    if (typeof message === "string") return message;
  }
  return "Failed to send recommendation";
};

export type InviteResult = {
  email: string;
  success: boolean;
  message?: string;
};

/**
 * Invites teammates by email. Mirrors the Admin Panel's `send_invitation` call
 * (`user_type: "cip_users"`), but on the non-admin CIP path. Because onboarding
 * only collects an email, we send a detectable placeholder name that the invited
 * user overwrites with their real name during their own onboarding.
 *
 * Each email is sent independently so one failure does not block the others; the
 * per-email outcome is returned to the caller.
 */
export const inviteTeamMembers = async (payload: {
  emails: string[];
  clinicIds: number[];
  // Emails (case-insensitive) to invite as Team Management Admin. Any email not
  // listed here is invited as a regular Member. Optional — defaults to all
  // Members. Matched by value (not index) so it is robust to blank/dup removal.
  adminEmails?: string[];
}): Promise<Result<InviteResult[]>> => {
  const emails = payload.emails
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  const adminSet = new Set(
    (payload.adminEmails || []).map((email) => email.trim().toLowerCase())
  );

  if (emails.length === 0) {
    return { success: true, data: [] };
  }

  try {
    const headers = await authHeaders();
    const results = await Promise.all(
      emails.map(async (email): Promise<InviteResult> => {
        try {
          // Same payload shape the Admin Panel sends for a `cip_users` invite.
          // dob/height_cm/weight_kg are required by the endpoint but irrelevant
          // for a staff invite, so we send the same fixed placeholders the Admin
          // Panel uses for its hidden fields (dob 1990-01-01, 180cm, 70kg).
          const response = await APIProvider.POST(
            ROUTES.inviteUsers,
            {
              first_name: PLACEHOLDER_FIRST_NAME,
              last_name: PLACEHOLDER_LAST_NAME,
              email,
              user_type: "cip_users",
              is_cpro_app_member: false,
              is_otp_enabled: false,
              clinic_ids: payload.clinicIds,
              // Optional secondary roles only. The backend assigns user_type
              // ("cip_users") as the required primary Cognito group.
              groups: [],
              // Team Management Admin role is granted via this explicit flag
              // (the backend authorizes the caller); defaults to Member.
              is_team_management_admin: adminSet.has(email.toLowerCase()),
              dob: PLACEHOLDER_DOB,
              height_cm: PLACEHOLDER_HEIGHT_CM,
              weight_kg: PLACEHOLDER_WEIGHT_KG,
              send_invitation: true,
            },
            headers
          );
          const error = inviteErrorMessage(response);
          return error
            ? { email, success: false, message: error }
            : { email, success: true };
        } catch (error) {
          return {
            email,
            success: false,
            message: messageFrom(error, "Failed to send invitation"),
          };
        }
      })
    );

    // Always hand back the per-email outcomes (even when every invite failed) so
    // the caller can distinguish benign failures like "already exists" from
    // genuinely retryable ones. `success: false` is reserved for a transport-level
    // failure (the catch below), where no per-email result could be produced.
    return { success: true, data: results };
  } catch (error) {
    return {
      success: false,
      error: { message: messageFrom(error, "Failed to send invitations") },
    };
  }
};

/**
 * Member "recommend CIP" referral. Instead of creating teammate accounts, this
 * asks the backend to email each address a soft intro — a link to the Circadia
 * Intelligence Platform and a note to email support@circadia.health for access.
 *
 * FRONTEND-ONLY FOR NOW: the backend endpoint (`ROUTES.referColleagues`) is NOT
 * implemented yet, so these calls will 404 until it lands. The UI already treats
 * a failed send as non-blocking (the member can always continue onboarding), and
 * this is the intended request/response contract for the future backend:
 *   POST { emails: string[], clinicIds: number[] }  ->  per-email outcomes.
 *
 * Mirrors `inviteTeamMembers`' per-email, never-throw shape so the step can reuse
 * the same outcome-handling code.
 */
export const referColleagues = async (payload: {
  emails: string[];
  clinicIds: number[];
}): Promise<Result<InviteResult[]>> => {
  const emails = payload.emails
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  if (emails.length === 0) {
    return { success: true, data: [] };
  }

  try {
    const headers = await authHeaders();
    const results = await Promise.all(
      emails.map(async (email): Promise<InviteResult> => {
        try {
          const response = await APIProvider.POST(
            ROUTES.referColleagues,
            { email, clinic_ids: payload.clinicIds },
            headers
          );
          const error = referralErrorMessage(response);
          return error
            ? { email, success: false, message: error }
            : { email, success: true };
        } catch (error) {
          return {
            email,
            success: false,
            message: messageFrom(error, "Failed to send recommendation"),
          };
        }
      })
    );
    return { success: true, data: results };
  } catch (error) {
    return {
      success: false,
      error: { message: messageFrom(error, "Failed to send recommendations") },
    };
  }
};
